import axios from 'axios';
import api from './api';
import { config } from '../config/environment';
import { adminApi } from './adminApi';

// --- DTOs ---

export type QuestionType =
    | 'MCQ'
    | 'TRUE_FALSE'
    | 'SHORT_ANSWER'
    | 'DRAG_DROP'
    | 'MATCHING'
    | 'ORDERING'
    | 'FILL_BLANK'
    | 'NUMERIC'
    | 'MULTIPLE_SELECT'
    | 'MENTAL_ARITHMETIC';

export interface QuizQuestionDTO {
    id: string; // UUID
    type: QuestionType;
    content: string; // Markdown supported
    mediaUrl?: string; // Optional image/video
    options?: any[]; // For MCQ/Ordering
    correctAnswer: any; // Structure depends on type
    explanation?: string;
    points: number;
}

export interface QuizConfigDTO {
    maxQuestions?: number;
    minPassPercent?: number; // e.g. 80
    timeLimitMinutes?: number;
    maxRetries?: number;
    allowAiGeneration?: boolean;
}

export interface ResolvedQuizConfigurationDTO extends QuizConfigDTO {
    source: 'COURSE' | 'TOPIC' | 'SUBJECT' | 'SYSTEM';
    sourceId: number;
}

export interface StaticQuizDTO extends QuizConfigDTO {
    id?: number;
    courseId?: number;
    topicId?: number;
    title: string;
    questions: QuizQuestionDTO[];
    status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
    type: string; // 'PRACTICE' | 'EXAM'
    stats?: {
        attempts: number;
        avgScore: number;
        rating: number; // 1-5
        uniqueUsers: number;
    };
}

export interface QuizAttemptDTO {
    id?: number;
    quizId: number;
    userId: number;
    startedAt: string;
    completedAt?: string;
    score: number;
    answers: Record<string, any>; // questionId -> answer
    passed: boolean;
}

export interface QuizAttemptResponseDTO {
    id: number;
    quizId?: number | null;
    topicId: number;
    quizTitle?: string;
    topicTitle?: string;
    courseId?: number;
    score: number;
    totalQuestions: number;
    correctAnswers: number;
    timeTakenMinutes?: number;
    status: string;
    startedAt: string;
    completedAt: string;
    answers?: Record<string, any>;
    passed: boolean;
    agentGenerated?: boolean;
    passThresholdPercent?: number;
}

export interface PrerequisiteSuggestionDTO {
    blockedTopicId: number;
    suggestedTopicIds: number[];
    reason: string; // "Failed Max Retries"
}

// --- API Service ---
const CORE_API_URL = `${config.coreApiUrl}/api`;

// Curriculum API with auth (same token as admin so curriculum service accepts admin users)
const curriculumApi = axios.create({
    baseURL: CORE_API_URL,
    timeout: 30000,
    headers: { 'Content-Type': 'application/json' },
});
curriculumApi.interceptors.request.use((cfg) => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
});

// Admin Methods (require auth for curriculum service)
export const getAdminQuizzes = async (topicId: number): Promise<StaticQuizDTO[]> => {
    const response = await curriculumApi.get(`/curriculum/quizzes/topic/${topicId}`);
    return response.data;
};

export const saveStaticQuiz = async (topicId: number, quiz: Partial<StaticQuizDTO>): Promise<StaticQuizDTO> => {
    const response = await curriculumApi.post(`/curriculum/quizzes/topic/${topicId}`, quiz);
    return response.data;
};

export const deleteQuiz = async (quizId: number): Promise<void> => {
    await curriculumApi.delete(`/curriculum/quizzes/${quizId}`);
};

/**
 * Generate quiz questions using AI (uses admin assessment generate-questions API and maps to quiz format).
 * Supported question types from AI: MCQ (MULTIPLE_CHOICE), TRUE_FALSE, SHORT_ANSWER.
 */
export const generateQuizQuestionsWithAI = async (
    topicId: number,
    config: {
        questionCount: number;
        questionTypes: string[]; // e.g. ['MULTIPLE_CHOICE', 'TRUE_FALSE', 'SHORT_ANSWER']
        difficultyLevel?: string;
        additionalInstructions?: string;
    }
): Promise<QuizQuestionDTO[]> => {
    const response = await adminApi.post<{ data?: Array<Record<string, unknown>> }>(
        `/api/admin/assessments/topic/${topicId}/generate-questions`,
        {
            questionCount: config.questionCount,
            questionTypes: config.questionTypes,
            difficultyLevel: config.difficultyLevel || 'MEDIUM',
            additionalInstructions: config.additionalInstructions || '',
        }
    );
    const raw = response.data?.data ?? response.data;
    const list = Array.isArray(raw) ? raw : [];
    return list.map((q: Record<string, unknown>, index: number) => {
        const questionType = (q.questionType as string) || 'MULTIPLE_CHOICE';
        const quizType = mapAssessmentTypeToQuizType(questionType);
        const options = q.options as string[] | undefined;
        let correctAnswer: any = q.correctAnswer;
        if (questionType === 'TRUE_FALSE' && typeof correctAnswer === 'string') {
            correctAnswer = correctAnswer.toLowerCase() === 'true' ? 'True' : 'False';
        }
        return {
            id: `ai-${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`,
            type: quizType,
            content: (q.questionText as string) || 'Question',
            options: options,
            correctAnswer: correctAnswer ?? (options && options[0] ? options[0] : ''),
            points: typeof q.points === 'number' ? q.points : 1,
            explanation: q.explanation as string | undefined,
        } as QuizQuestionDTO;
    });
};

function mapAssessmentTypeToQuizType(assessmentType: string): QuestionType {
    switch (assessmentType) {
        case 'MULTIPLE_CHOICE': return 'MCQ';
        case 'TRUE_FALSE': return 'TRUE_FALSE';
        case 'SHORT_ANSWER': return 'SHORT_ANSWER';
        case 'LONG_ANSWER': return 'SHORT_ANSWER';
        default: return 'MCQ';
    }
}

// Student Methods
export const getNextPracticeQuiz = async (topicId: number): Promise<StaticQuizDTO | PrerequisiteSuggestionDTO | null> => {
    try {
        // Fetch published quizzes from curriculum-service (only PUBLISHED status)
        const url = `${CORE_API_URL}/curriculum/quizzes/topic/${topicId}/published`;
        console.log('Fetching published quiz from:', url);
        
        const response = await axios.get(url);
        console.log('Quiz API response:', response.data);
        
        const quizzes: StaticQuizDTO[] = response.data;
        
        if (!quizzes || quizzes.length === 0) {
            console.log('No published quizzes found for topic:', topicId);
            return null;
        }
        
        // Return the first available published quiz
        const quiz = quizzes[0];
        console.log('Found published quiz:', quiz);
        return quiz;
    } catch (error: any) {
        console.error('Failed to fetch practice quiz:', error);
        console.error('Error details:', {
            message: error?.message,
            response: error?.response?.data,
            status: error?.response?.status,
            url: error?.config?.url
        });
        return null;
    }
};

export const submitQuizAttempt = async (
    quizId: number,
    topicId: number,
    answers: Record<string, any>,
    score: number,
    correctAnswers: number,
    timeTakenMinutes: number,
    passed: boolean
): Promise<QuizAttemptResponseDTO> => {
    const response = await api.post('/api/student/quiz/attempt', {
        quizId: Number(quizId),
        topicId: Number(topicId),
        answers,
        score: Math.round(score), // Ensure integer for backend
        correctAnswers: Number(correctAnswers),
        timeTakenMinutes: Number(timeTakenMinutes),
        passed: Boolean(passed)
    });
    return response.data.data; // ApiResponse wrapper
};

export const getQuizAttempts = async (quizId: number): Promise<QuizAttemptResponseDTO[]> => {
    const response = await api.get(`/api/student/quiz/attempts/quiz/${quizId}`);
    return response.data.data; // ApiResponse wrapper
};

export const getTopicQuizAttempts = async (topicId: number): Promise<QuizAttemptResponseDTO[]> => {
    const response = await api.get(`/api/student/quiz/attempts/topic/${topicId}`);
    return response.data.data; // ApiResponse wrapper
};

export const getCourseQuizAttempts = async (courseId: number): Promise<QuizAttemptResponseDTO[]> => {
    const response = await api.get(`/api/student/quiz/attempts/course/${courseId}`);
    return response.data.data;
};

/**
 * Quiz config layer keys understood by the backend.
 *
 * - `COURSE` / `TOPIC`              — unchanged.
 * - `CURRICULUM_SUBJECT`            — per-board layer (preferred name post-011).
 * - `CORE_SUBJECT`                  — universal baseline (added in changelog 011).
 * - `SUBJECT` (deprecated)          — backend alias for `CURRICULUM_SUBJECT`; kept
 *                                     for one release while UIs migrate.
 */
export type QuizConfigLevel = 'COURSE' | 'TOPIC' | 'CURRICULUM_SUBJECT' | 'CORE_SUBJECT' | 'SUBJECT';

export const getQuizConfig = async (level: QuizConfigLevel, entityId: number): Promise<QuizConfigDTO> => {
    const response = await curriculumApi.get(`/curriculum/quiz-config/${level}/${entityId}`);
    return response.data;
};

export const saveQuizConfig = async (level: QuizConfigLevel, entityId: number, config: Partial<QuizConfigDTO>): Promise<QuizConfigDTO> => {
    const response = await curriculumApi.put(`/curriculum/quiz-config/${level}/${entityId}`, config);
    return response.data;
};

export const resolveQuizConfig = async (
    courseId?: number,
    topicId?: number,
    curriculumSubjectId?: number,
    coreSubjectId?: number
): Promise<ResolvedQuizConfigurationDTO> => {
    const params = new URLSearchParams();
    if (courseId) params.append('courseId', courseId.toString());
    if (topicId) params.append('topicId', topicId.toString());
    if (curriculumSubjectId) params.append('curriculumSubjectId', curriculumSubjectId.toString());
    if (coreSubjectId) params.append('coreSubjectId', coreSubjectId.toString());

    const response = await curriculumApi.get(`/curriculum/quiz-config/resolve?${params.toString()}`);
    return response.data;
};

export const generateAIQuiz = async (topicId: number): Promise<StaticQuizDTO> => {
    // return axios.post(`${CORE_API_URL}/student/topics/${topicId}/quiz/generate`).then(res => res.data);
    return {} as StaticQuizDTO;
};
